import dayjs from 'dayjs';
import ExcelJS, { Row } from 'exceljs';
import { saveAs } from 'file-saver';

import { AGGREGATION_TYPE, AGGREGATION_TYPE_LABELS, VALUE_DIFFERENCE_DISPLAY } from 'components/View/constants';
import { CommunityOccupancy, CommunityType, UnitSystem } from 'types/communities';
import { CommunityContract } from 'types/contracts';
import { CommunityBaseIncentiveType } from 'types/incentives';
import { calculateOccupancy, groupAndSortCommunityContracts } from 'utils/data/communities';
import {
  convertAreaByUnitSystem,
  convertRatePerAreaByUnitSystem,
  formatDistanceFromKm,
  getAreaUnitLabel,
  getDistanceUnitLabel,
  getRatePerAreaUnitLabel,
  resolveUnitSystem,
} from 'utils/helpers/units';

import {
  CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW,
  CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON,
  CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW,
  CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON,
  CMA_SUMMARY_CARE_FEE_DATA_ROW,
  CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW,
  CMA_SUMMARY_COMMUNITY_CONTRACT_ROW,
  CMA_SUMMARY_CONTRACT_BENEFIT_DATA_ROW,
  CMA_SUMMARY_DATA_ROW_TYPE,
  CMA_SUMMARY_FEE_LABELS,
  CMA_SUMMARY_FEE_TITLES,
  CMA_SUMMARY_LIVING_TYPE_DATA_ROW,
  CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW,
  CMA_SUMMARY_MEDICATION_FEE_DATA_ROW,
  CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW,
  CMA_SUMMARY_NOT_APPLICABLE_VALUE,
  CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS,
  CMA_SUMMARY_ROOM_TYPE_GROUPING,
} from './constants';
import { CMAData, CMADataCellValue, CMAHeader, CMASummaryFilters } from './types';
import { getCMASummaryReviewSources } from './utils';
import { getComparisonSeriesValue, getHighlightFlag, getNumericSeriesValue } from './valueUtils';
import { COMMUNITY_CONTRACT_SELECTION_TYPE, COMMUNITY_CONTRACT_TYPE } from '../../constants';

const getExceljsStyleObject = (styleObject: any) => JSON.parse(JSON.stringify(styleObject));

const COLORS = {
  DEFAULT_HEADER: '#89919d',
  APARTMENT_GROUP_DATA_BACKGROUND: '#E6E6E6',
};

const HIGHLIGHT_TEXT_COLOR = '#E8A947';

const BORDERS = {
  REGULAR_CELL: {
    top: { style: 'thin' as ExcelJS.BorderStyle, color: { argb: 'FF000000' } },
    bottom: { style: 'thin' as ExcelJS.BorderStyle, color: { argb: 'FF000000' } },
    right: { style: 'thin' as ExcelJS.BorderStyle, color: { argb: 'FF000000' } },
    left: { style: 'thin' as ExcelJS.BorderStyle, color: { argb: 'FF000000' } },
  },
  HEADER_CELL: {
    top: { style: 'medium' as ExcelJS.BorderStyle, color: { argb: 'FF000000' } },
    left: { style: 'medium' as ExcelJS.BorderStyle, color: { argb: 'FF000000' } },
    bottom: { style: 'medium' as ExcelJS.BorderStyle, color: { argb: 'FF000000' } },
    right: { style: 'medium' as ExcelJS.BorderStyle, color: { argb: 'FF000000' } },
  },
};

const FONT_STYLES = {
  DEFAULT: getExceljsStyleObject({ size: 8, name: 'Helvetica Neue' }),
  BOLD: getExceljsStyleObject({ size: 8, name: 'Helvetica Neue', bold: true }),
};

const COLUMN_WIDTHS = {
  COLUMN1: 15.5,
  COLUMN2: 12.3,
  COMMUNITY: 28.4,
  COMPETITOR: 25,
};

const LINE_HEIGHT_PX = 10; // For Helvetica Neue
const CHAR_WIDTH_PX = 4.8; // Average char width in Excel units for Helvetica Neue

const hexToARGB = (hex: string) => {
  return 'FF' + hex.replace('#', '').toUpperCase();
};

const lightenWithOpacity = (hex: string, alpha = 0.7) => {
  const h = hex.replace('#', '').slice(0, 6);
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);

  const blend = (c: number) => Math.round(c * (1 - alpha) + 255 * alpha);

  const toHex = (c: number) => c.toString(16).padStart(2, '0');

  return `#${toHex(blend(r))}${toHex(blend(g))}${toHex(blend(b))}`;
};

const isMedicationFeeRowType = (rowType?: string) =>
  rowType === CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW || rowType === CMA_SUMMARY_MEDICATION_FEE_DATA_ROW;

const isContractBenefitsRecord = (value: unknown): value is Record<string, string[]> => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return false;
  }

  return Object.values(value as Record<string, unknown>).every(
    (benefits) => Array.isArray(benefits) && benefits.every((benefit) => typeof benefit === 'string')
  );
};

const getFeeTitleByRowType = (rowType?: string) =>
  isMedicationFeeRowType(rowType) ? CMA_SUMMARY_FEE_TITLES.MEDICATION : CMA_SUMMARY_FEE_TITLES.CARE;

const getFeeLabelByRowType = (rowType?: string) =>
  isMedicationFeeRowType(rowType) ? CMA_SUMMARY_FEE_LABELS.MEDICATION : CMA_SUMMARY_FEE_LABELS.CARE;

const getContractsCellValue = (community?: CommunityType) => {
  const contracts = ((community as any)?.contracts ?? []) as CommunityContract[];

  if (!contracts.length) {
    return '';
  }

  const groupedContracts = groupAndSortCommunityContracts(contracts);
  return groupedContracts
    .map((group) => {
      if (group.type === COMMUNITY_CONTRACT_TYPE.RENTAL) {
        return group.name;
      }
      const percentages = group.percentages.map((percentage) => `${percentage}%`).join(', ');
      return percentages ? `${group.name} (${percentages})` : group.name;
    })
    .join(' ');
};

const getRowLivingTypeTitle = (rowMetadata: CMAHeader): string | undefined => {
  if (typeof rowMetadata.subtitle === 'string') {
    return rowMetadata.subtitle;
  }
  if (Array.isArray(rowMetadata.subtitle) && typeof rowMetadata.subtitle[0] === 'string') {
    return rowMetadata.subtitle[0];
  }
  return undefined;
};

const getSectionTitle = (livingTypeTitle: string | undefined, sectionTitle: string, contractLabel?: string) => {
  if (!livingTypeTitle) {
    return undefined;
  }

  const trimmedContractLabel = contractLabel?.trim();
  return trimmedContractLabel
    ? `${livingTypeTitle} - ${trimmedContractLabel} - ${sectionTitle}`
    : `${livingTypeTitle} - ${sectionTitle}`;
};

const getContractComparisonLabel = (selectionType?: number) => {
  if (selectionType === COMMUNITY_CONTRACT_SELECTION_TYPE.EXACT) {
    return 'Exact Comparison';
  }
  if (selectionType === COMMUNITY_CONTRACT_SELECTION_TYPE.APPROXIMATE) {
    return 'Approximate Comparison';
  }
  return undefined;
};

export const handleDownloadExcel = (
  communities: CommunityType[],
  filters: CMASummaryFilters,
  CMASummaryData?: CMAData
) => {
  if (!CMASummaryData || !communities?.length) {
    return;
  }

  const communityColor = communities[0].spreadsheet_export_color || COLORS.DEFAULT_HEADER;
  const communityColorARGB = hexToARGB(communityColor);
  const lighterColor = hexToARGB(lightenWithOpacity(communityColor));
  const baseCommunity = communities[0];
  const unitSystem = resolveUnitSystem(baseCommunity);
  const distanceUnitLabel = getDistanceUnitLabel(unitSystem);
  const areaUnitLabel = getAreaUnitLabel(unitSystem);
  const ratePerAreaUnitLabel = getRatePerAreaUnitLabel(unitSystem);

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('CMA Summary');
  worksheet.views = [{ state: 'frozen', ySplit: 1 }];

  worksheet.getColumn(1).width = COLUMN_WIDTHS.COLUMN1;
  worksheet.getColumn(2).width = COLUMN_WIDTHS.COLUMN2;

  const exportColumnCount = (CMASummaryData[0]?.length ?? communities.length + 1) + 1;
  for (let columnIndex = 3; columnIndex <= exportColumnCount; columnIndex += 1) {
    worksheet.getColumn(columnIndex).width = columnIndex === 3 ? COLUMN_WIDTHS.COMMUNITY : COLUMN_WIDTHS.COMPETITOR;
  }

  let currentContractLivingType: string | undefined;
  let currentContractTypes: string[] = [];
  let currentContractLabel: string | undefined;
  let currentContractComparisonLabel: string | undefined;
  let pendingContractBenefitsRows: Array<{ rowMetadata: CMAHeader; rowContent: CMADataCellValue[] }> = [];

  const flushPendingContractBenefits = (livingTypeTitle?: string, numberOfColumns?: number) => {
    if (!livingTypeTitle || !numberOfColumns || !pendingContractBenefitsRows.length) {
      return;
    }

    appendApartmentTypeGroupHeader(
      worksheet,
      livingTypeTitle,
      numberOfColumns,
      lighterColor,
      currentContractLabel,
      currentContractComparisonLabel
    );
    appendContractTypeSummaryRow(worksheet, currentContractTypes);
    pendingContractBenefitsRows.forEach(({ rowMetadata, rowContent }) => {
      appendContractBenefitsRows(worksheet, rowMetadata, rowContent);
    });
    pendingContractBenefitsRows = [];
  };

  CMASummaryData.forEach((CMArow) => {
    const rowMetadata = CMArow[0];
    const [, ...rowContent] = CMArow;
    const rowValues = ['', '', ...rowContent];
    let row;

    switch (rowMetadata?.type) {
      case CMA_SUMMARY_DATA_ROW_TYPE.COMMUNITY_NAME: {
        appendCommunitiesTitleRow(rowValues, worksheet, communityColorARGB);
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.ADDRESS:
        appendGeneralDataRow(rowMetadata.type, rowValues, worksheet);
        break;
      case CMA_SUMMARY_DATA_ROW_TYPE.DISTANCE: {
        const rowContentDistance = rowValues.map((item: CMADataCellValue) => {
          if (typeof item === 'number') {
            return `${formatDistanceFromKm(item, unitSystem, 1)} ${distanceUnitLabel}`;
          }
          return item || '';
        });
        appendGeneralDataRow(rowMetadata.type, rowContentDistance, worksheet);
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.NOTES: {
        appendGeneralDataRow(
          rowMetadata.type,
          rowValues.map((item) => item || ''),
          worksheet
        );
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.CARE_TYPES: {
        const careTypeRows = rowValues.map((item: CMADataCellValue) => {
          if (item === CMA_SUMMARY_NOT_APPLICABLE_VALUE) {
            return CMA_SUMMARY_NOT_APPLICABLE_VALUE;
          }
          if (Array.isArray(item) && item.length > 0) {
            return (item as (string | number | boolean | null | undefined)[]).filter(Boolean).join('\n');
          }
          return '';
        });
        appendGeneralDataRow(rowMetadata.type, careTypeRows, worksheet);
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.CONTRACT_TYPE: {
        const contractsRow = rowValues.map((item: CMADataCellValue) => {
          if (item === CMA_SUMMARY_NOT_APPLICABLE_VALUE) {
            return CMA_SUMMARY_NOT_APPLICABLE_VALUE;
          }
          if (!Array.isArray(item) || item.length === 0) {
            return '';
          }
          return getContractsCellValue({ contracts: item } as unknown as CommunityType);
        });
        appendGeneralDataRow(rowMetadata.type, contractsRow, worksheet);
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.WEBSITE:
        appendGeneralDataRow(rowMetadata.type, rowValues, worksheet);
        break;
      case CMA_SUMMARY_DATA_ROW_TYPE.PHONE:
        appendGeneralDataRow(rowMetadata.type, rowValues, worksheet);
        break;
      case CMA_SUMMARY_DATA_ROW_TYPE.YEAR_OPENED:
        appendGeneralDataRow(rowMetadata.type, rowValues, worksheet);
        break;
      case CMA_SUMMARY_DATA_ROW_TYPE.SHOP_DATE: {
        const rowContentWithFormattedDates = rowValues.map((item: CMADataCellValue) =>
          item === CMA_SUMMARY_NOT_APPLICABLE_VALUE
            ? CMA_SUMMARY_NOT_APPLICABLE_VALUE
            : item
              ? dayjs(item as string).format('MMM YYYY')
              : ''
        );
        appendGeneralDataRow(rowMetadata.type, rowContentWithFormattedDates, worksheet);
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.REVIEWS: {
        const overallRatings = ['', '', ...rowContent.map((item: any) => normalizeComparisonData(item?.[0], filters))];
        appendGeneralDataRow(
          CMA_SUMMARY_DATA_ROW_TYPE.REVIEWS,
          overallRatings,
          worksheet,
          filters,
          `Rating (${rowMetadata.subtitle?.[0]})`
        );

        const overallReviews = ['', '', ...rowContent.map((item: any) => normalizeComparisonData(item?.[1], filters))];
        appendGeneralDataRow(
          CMA_SUMMARY_DATA_ROW_TYPE.NUMBER_OF_REVIEWS,
          overallReviews,
          worksheet,
          filters,
          `Number of Reviews (${rowMetadata.subtitle?.[0]})`
        );

        const sources = Array.isArray(rowMetadata.subtitle) ? rowMetadata.subtitle.slice(1) : [];

        sources.forEach((source, sourceIndex) => {
          const sourceRatings = [
            '',
            '',
            ...rowContent.map((item: any) => {
              const val = item?.[sourceIndex + 2];
              return val ? val.toString().split('·')[0].trim() : '-';
            }),
          ] as CMADataCellValue[];

          const sourceReviews = [
            '',
            '',
            ...rowContent.map((item: any) => {
              const val = item?.[sourceIndex + 2];
              return val ? (String(val).match(/·\s*([^,]+)/)?.[1] ?? '').trim() : '-';
            }),
          ] as CMADataCellValue[];

          appendGeneralDataRow(
            CMA_SUMMARY_DATA_ROW_TYPE.REVIEWS,
            sourceRatings,
            worksheet,
            filters,
            `Rating (${source})`
          );
          appendGeneralDataRow(
            CMA_SUMMARY_DATA_ROW_TYPE.REVIEWS,
            sourceReviews,
            worksheet,
            filters,
            `Number of Reviews (${source})`
          );
        });

        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.OCCUPANCY: {
        const rowContentOccupancies = rowValues.map((item: CMADataCellValue) => {
          if (item === CMA_SUMMARY_NOT_APPLICABLE_VALUE) {
            return CMA_SUMMARY_NOT_APPLICABLE_VALUE;
          }
          if (typeof item === 'number') {
            return `${item.toFixed(0)}%`;
          }
          if (Array.isArray(item) && item.length > 0) {
            const occupancies = item as CommunityOccupancy[];
            const { totalUnits, occupiedUnits, occupancyPercentage } = calculateOccupancy(occupancies);
            return `${occupancyPercentage}%${totalUnits ? ` (${occupiedUnits}/${totalUnits})` : ''}`.trim();
          }
          return '';
        });

        appendGeneralDataRow(rowMetadata.type, rowContentOccupancies, worksheet);
        break;
      }
      case CMA_SUMMARY_LIVING_TYPE_DATA_ROW: {
        appendLivingTypeAndOccupancyRows(
          rowValues,
          worksheet,
          rowMetadata.title || '',
          communityColorARGB,
          rowMetadata.hasOccupancies
        );
        break;
      }
      case CMA_SUMMARY_COMMUNITY_CONTRACT_ROW: {
        currentContractLivingType = getRowLivingTypeTitle(rowMetadata);
        currentContractLabel = rowMetadata.title || undefined;
        currentContractComparisonLabel = getContractComparisonLabel(filters.contractSelectionType);
        currentContractTypes = rowContent.map((item) => {
          if (Array.isArray(item) && item.length) {
            const contractName = (item[0] as string) || '';
            const percentage = item[1] ? `${item[1]}%` : '';
            if (contractName && percentage) {
              return `${contractName} ${percentage}`;
            }
            return contractName || '';
          }
          return '';
        });
        pendingContractBenefitsRows = [];
        break;
      }
      case CMA_SUMMARY_CONTRACT_BENEFIT_DATA_ROW: {
        pendingContractBenefitsRows.push({ rowMetadata, rowContent });
        break;
      }
      case CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW: {
        flushPendingContractBenefits(getRowLivingTypeTitle(rowMetadata) || currentContractLivingType, rowValues.length);
        if (filters.roomTypeGrouping !== CMA_SUMMARY_ROOM_TYPE_GROUPING.DONT_GROUP) {
          appendApartmentTypeGroupDataRows(
            worksheet,
            rowMetadata,
            rowValues.length,
            rowContent,
            filters,
            lighterColor,
            unitSystem,
            areaUnitLabel,
            ratePerAreaUnitLabel,
            currentContractLabel,
            currentContractComparisonLabel
          );
        }
        break;
      }
      case CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW: {
        flushPendingContractBenefits(getRowLivingTypeTitle(rowMetadata) || currentContractLivingType, rowValues.length);
        const subtitle = Array.isArray(rowMetadata.subtitle) ? rowMetadata.subtitle[0] : rowMetadata.subtitle ?? '';
        appendApartmentTypeGroupHeader(
          worksheet,
          subtitle,
          rowValues.length,
          lighterColor,
          currentContractLabel,
          currentContractComparisonLabel
        );
        appendApartmentTypeDataRows(
          worksheet,
          rowMetadata,
          rowContent,
          rowValues.length,
          filters,
          unitSystem,
          areaUnitLabel,
          ratePerAreaUnitLabel
        );
        break;
      }
      case CMA_SUMMARY_APARTMENT_TYPE_DATA_ROW_ROOM_COMPARISON:
      case CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON: {
        flushPendingContractBenefits(getRowLivingTypeTitle(rowMetadata) || currentContractLivingType, rowValues.length);
        appendRoomComparisonRow(
          worksheet,
          rowMetadata,
          rowContent,
          rowValues.length,
          filters,
          lighterColor,
          currentContractLabel,
          currentContractComparisonLabel
        );
        break;
      }
      case CMA_SUMMARY_LIVING_TYPE_FEE_DATA_ROW: {
        flushPendingContractBenefits(getRowLivingTypeTitle(rowMetadata) || currentContractLivingType, rowValues.length);
        appendCommunityFeeRows(
          worksheet,
          rowMetadata,
          rowValues.length,
          rowContent,
          filters,
          lighterColor,
          currentContractLabel
        );
        break;
      }
      case CMA_SUMMARY_CARE_FEE_GROUP_DATA_ROW: {
        appendFeesGroupRows(
          worksheet,
          rowMetadata,
          rowValues.length,
          rowContent,
          filters,
          lighterColor,
          filters.careLevelsStatsType,
          currentContractLabel
        );
        break;
      }
      case CMA_SUMMARY_MEDICATION_FEE_GROUP_DATA_ROW: {
        appendFeesGroupRows(
          worksheet,
          rowMetadata,
          rowValues.length,
          rowContent,
          filters,
          lighterColor,
          filters.medicationLevelsStatsType,
          currentContractLabel
        );
        break;
      }
      case CMA_SUMMARY_CARE_FEE_DATA_ROW:
      case CMA_SUMMARY_MEDICATION_FEE_DATA_ROW: {
        appendFeeLevelRows(worksheet, rowMetadata, rowContent);
        break;
      }
      case CMA_SUMMARY_DATA_ROW_TYPE.INCENTIVES: {
        row = worksheet.addRow(rowValues);
        row.eachCell((cell, index) => {
          cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: communityColorARGB },
          };
          cell.font = { ...FONT_STYLES.BOLD, color: { argb: 'FFFFFFFF' } };
          cell.alignment = {
            vertical: 'middle',
            horizontal: 'left',
          };
        });

        worksheet.mergeCells(row.number, 1, row.number, rowValues.length);
        const cell = worksheet.getCell(row.number, 1);
        cell.value = 'Incentives';
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } },
        };

        appendIncentives(rowContent, worksheet, rowValues.length);
        break;
      }
    }
  });

  workbook.xlsx.writeBuffer().then((buffer) => {
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const communityName = communities[0].comunity_name.split(' ').join('_');
    const currentDate = new Date().toLocaleDateString();
    const filename = `CMA_Summary_${communityName}_${currentDate}`;
    saveAs(blob, `${filename}.xlsx`);
  });
};

const appendCommunitiesTitleRow = (
  rowContent: CMADataCellValue[],
  worksheet: ExcelJS.Worksheet,
  communityColor: string
) => {
  const row = worksheet.addRow(rowContent);
  row.eachCell((cell, index) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: communityColor },
    };
    cell.font = { ...FONT_STYLES.BOLD, color: { argb: 'FFFFFFFF' } };
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'left',
    };
  });

  worksheet.mergeCells(row.number, 1, row.number, 2);
  worksheet.getCell(row.number, 1).value = 'Community';
  worksheet.getCell(row.number, 1).alignment.horizontal = 'right';
};

const appendGeneralDataRow = (
  type: string,
  rowContent: CMADataCellValue[],
  worksheet: ExcelJS.Worksheet,
  filters?: CMASummaryFilters,
  title?: string
) => {
  const row = worksheet.addRow(rowContent);
  row.height = 16; // 21px is 16 points

  let rowTitle;
  let horizontalAlignment: ExcelJS.Alignment['horizontal'];
  let wrapText = false;
  switch (type) {
    case CMA_SUMMARY_DATA_ROW_TYPE.ADDRESS:
      rowTitle = 'Address';
      horizontalAlignment = 'left';
      wrapText = true;
      break;
    case CMA_SUMMARY_DATA_ROW_TYPE.DISTANCE:
      rowTitle = 'Distance';
      horizontalAlignment = 'right';
      break;
    case CMA_SUMMARY_DATA_ROW_TYPE.NOTES: {
      rowTitle = 'Community Notes';
      horizontalAlignment = 'left';
      wrapText = true;
      break;
    }
    case CMA_SUMMARY_DATA_ROW_TYPE.CARE_TYPES: {
      rowTitle = 'Care Types';
      horizontalAlignment = 'right';
      wrapText = true;
      break;
    }
    case CMA_SUMMARY_DATA_ROW_TYPE.CONTRACT_TYPE: {
      rowTitle = 'Contract Type';
      horizontalAlignment = 'right';
      wrapText = true;
      break;
    }
    case CMA_SUMMARY_DATA_ROW_TYPE.WEBSITE:
      rowTitle = 'Website';
      horizontalAlignment = 'left';
      wrapText = true;
      break;
    case CMA_SUMMARY_DATA_ROW_TYPE.PHONE:
      rowTitle = 'Phone';
      horizontalAlignment = 'left';
      break;
    case CMA_SUMMARY_DATA_ROW_TYPE.YEAR_OPENED:
      rowTitle = 'Year Opened';
      horizontalAlignment = 'left';
      break;
    case CMA_SUMMARY_DATA_ROW_TYPE.SHOP_DATE:
      rowTitle = 'Shop Date';
      horizontalAlignment = 'right';
      break;
    case CMA_SUMMARY_DATA_ROW_TYPE.REVIEWS: {
      rowTitle = title;
      horizontalAlignment = 'right';
      break;
    }
    case CMA_SUMMARY_DATA_ROW_TYPE.NUMBER_OF_REVIEWS: {
      const reviewSources = getCMASummaryReviewSources(filters!);
      rowTitle = `Number of reviews (${reviewSources})`;
      horizontalAlignment = 'right';
      break;
    }
    case CMA_SUMMARY_DATA_ROW_TYPE.OCCUPANCY: {
      rowTitle = `Occupancy overall`;
      horizontalAlignment = 'left';
      break;
    }
  }

  row.eachCell((cell, index) => {
    cell.font = FONT_STYLES.DEFAULT;
    cell.alignment = {
      vertical: 'middle',
      horizontal: horizontalAlignment,
      wrapText,
    };
    cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);

    if (type === CMA_SUMMARY_DATA_ROW_TYPE.WEBSITE && index > 1) {
      const rawValue = cell.value?.toString().trim();

      if (rawValue) {
        const cellValue: ExcelJS.CellHyperlinkValue = {
          text: rawValue,
          hyperlink: rawValue,
        };
        cell.value = cellValue;
      } else {
        cell.value = '';
      }
    }
  });

  worksheet.mergeCells(row.number, 1, row.number, 2);
  worksheet.getCell(row.number, 1).value = rowTitle;
  worksheet.getCell(row.number, 1).alignment.horizontal = 'right';
  adjustRowHeightForWrappedText(row);
};

const appendLivingTypeAndOccupancyRows = (
  rowContent: CMADataCellValue[],
  worksheet: ExcelJS.Worksheet,
  title: string,
  communityColor: string,
  hasOccupancies?: boolean
) => {
  let row = worksheet.addRow(rowContent);
  worksheet.mergeCells(row.number, 1, row.number, rowContent.length);
  const mergedCell = worksheet.getCell(row.number, 1);
  mergedCell.value = title;
  worksheet.getCell(row.number, 1).alignment = {
    vertical: 'middle',
    horizontal: 'center',
  };
  mergedCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: communityColor },
  };
  mergedCell.font = { ...FONT_STYLES.BOLD, color: { argb: 'FFFFFFFF' } };
  mergedCell.border = getExceljsStyleObject(BORDERS.HEADER_CELL);
  if (hasOccupancies) {
    const occupancyPercentages = rowContent
      .slice(2)
      .filter((i) => i)
      .flatMap((item) => {
        const occupancies = item as [number, number, number, boolean];
        return occupancies.length && occupancies[0] ? `${Math.round(occupancies[0])}%` : '';
      });
    row = worksheet.addRow(['', '', ...occupancyPercentages]);
    row.eachCell((cell, index) => {
      cell.font = FONT_STYLES.DEFAULT;
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'right',
      };
      cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
    });
    worksheet.mergeCells(row.number, 1, row.number, 2);
    worksheet.getCell(row.number, 1).value = 'Occupancy';
    worksheet.getCell(row.number, 1).alignment.horizontal = 'right';

    const hasNumberOfUnits = rowContent
      .slice(2)
      .filter((i) => i)
      .some((item) => {
        const occupancies = item as [number, number, number, boolean];
        return occupancies.length && occupancies[1] && occupancies[2];
      });
    if (hasNumberOfUnits) {
      const numberOfUnitsRow = rowContent
        .slice(2)
        .filter((i) => i)
        .flatMap((item) => {
          const occupancies = item as [number, number, number, boolean];
          return occupancies.length && occupancies[1] ? Math.round(occupancies[1]) : '';
        });
      row = worksheet.addRow(['', '', ...numberOfUnitsRow]);
      row.eachCell((cell, index) => {
        cell.font = FONT_STYLES.DEFAULT;
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'right',
        };
        cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
      });
      worksheet.mergeCells(row.number, 1, row.number, 2);
      worksheet.getCell(row.number, 1).value = 'Total number of units';
      worksheet.getCell(row.number, 1).alignment.horizontal = 'right';

      const numberOfOccupiedUnitsRow = rowContent
        .slice(2)
        .filter((i) => i)
        .flatMap((item) => {
          const occupancies = item as [number, number, number, boolean];
          return occupancies.length && occupancies[1] && occupancies[2]
            ? Math.round(occupancies[1] - occupancies[2])
            : '';
        });
      row = worksheet.addRow(['', '', ...numberOfOccupiedUnitsRow]);
      row.eachCell((cell, index) => {
        cell.font = FONT_STYLES.DEFAULT;
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'right',
        };
        cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
      });
      worksheet.mergeCells(row.number, 1, row.number, 2);
      worksheet.getCell(row.number, 1).value = 'Occupied number of units';
      worksheet.getCell(row.number, 1).alignment.horizontal = 'right';
    }
  }
};

const appendIncentives = (rowContent: CMADataCellValue[], worksheet: ExcelJS.Worksheet, totalColumnsNumber: number) => {
  const rowIncentivesWithContent = rowContent.map((incentives: CMADataCellValue) => {
    if (Array.isArray(incentives)) {
      return (incentives as CommunityBaseIncentiveType[]).filter((incentive: any) => Boolean(incentive.description));
    }

    if (incentives === CMA_SUMMARY_NOT_APPLICABLE_VALUE) {
      return [{ description: CMA_SUMMARY_NOT_APPLICABLE_VALUE }];
    }

    return [];
  });
  const incentivesMaxCount = Math.max(...rowIncentivesWithContent.map((incentives: any[]) => incentives.length));

  for (let i = 0; i < incentivesMaxCount; i++) {
    const incentivesDescriptions = rowIncentivesWithContent.map((incentives: any[]) =>
      incentives[i] ? incentives[i].description : undefined
    );
    const incentivesCreatedDates = rowIncentivesWithContent.map((incentives: any[]) =>
      incentives[i]?.created_at ? dayjs(incentives[i].created_at).format('MMM DD YYYY') : undefined
    );

    const incentivesCreatedDatesRow = worksheet.addRow(['', '', ...incentivesCreatedDates.map((item) => item || '')]);
    const incentivesDescriptionsRow = worksheet.addRow(['', '', ...incentivesDescriptions.map((item) => item || '')]);

    worksheet.mergeCells(incentivesCreatedDatesRow.number, 1, incentivesDescriptionsRow.number, 1);
    const mergedCell = worksheet.getCell(incentivesCreatedDatesRow.number, 1);
    mergedCell.value = 'Incentive';
    const dateLabelCell = worksheet.getCell(incentivesCreatedDatesRow.number, 2);
    dateLabelCell.value = 'Date';
    const descriptionLabelCell = worksheet.getCell(incentivesDescriptionsRow.number, 2);
    descriptionLabelCell.value = 'Description';

    incentivesCreatedDatesRow.eachCell((cell, index) => {
      cell.font = FONT_STYLES.DEFAULT;
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'right',
      };
      cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
      if (index === totalColumnsNumber) {
        cell.border = { right: getExceljsStyleObject(BORDERS.REGULAR_CELL.right) };
      }
    });
    incentivesDescriptionsRow.eachCell((cell, index) => {
      cell.font = FONT_STYLES.DEFAULT;
      cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
      if (index === 2) {
        cell.alignment = {
          vertical: 'top',
          horizontal: 'right',
        };
      } else if (index > 2) {
        cell.alignment = {
          vertical: 'top',
          horizontal: 'left',
          wrapText: true,
        };
      }
      if (index === totalColumnsNumber) {
        cell.border = { right: getExceljsStyleObject(BORDERS.REGULAR_CELL.right) };
      }
    });
    adjustRowHeightForWrappedText(incentivesDescriptionsRow);

    worksheet.getCell(incentivesCreatedDatesRow.number, totalColumnsNumber).border = {
      right: getExceljsStyleObject(BORDERS.HEADER_CELL.right),
    };
    worksheet.getCell(incentivesDescriptionsRow.number, totalColumnsNumber).border = {
      right: getExceljsStyleObject(BORDERS.HEADER_CELL.right),
    };
    for (let i = 0; i < totalColumnsNumber; i += 1) {
      if (i === 0) {
        const cell = worksheet.getCell(incentivesCreatedDatesRow.number, i + 1);
        cell.border = {
          bottom: getExceljsStyleObject(BORDERS.HEADER_CELL.bottom),
        };
      } else {
        const cell = worksheet.getCell(incentivesDescriptionsRow.number, i + 1);
        if (cell.value === null) {
          cell.value = '';
        }
        cell.border = {
          ...cell.border,
          bottom: getExceljsStyleObject(BORDERS.HEADER_CELL.bottom),
        };
      }
    }
  }
};

export const adjustRowHeightForWrappedText = (row: Row) => {
  let maxLines = 1;

  let containsWrappedText = false;

  row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
    if (cell.alignment?.wrapText && typeof cell.value === 'string') {
      containsWrappedText = true;
      const colWidth = cell.worksheet.getColumn(colNumber).width ?? 8; // Excel "character" units
      const colWidthPx = colWidth * CHAR_WIDTH_PX;

      const explicitLines = cell.value.split(/\r?\n/);

      let totalLines = 0;
      for (const line of explicitLines) {
        const estWidthPx = line.length * CHAR_WIDTH_PX;
        const wrappedCount = Math.ceil(estWidthPx / colWidthPx) || 1;
        totalLines += wrappedCount;
      }

      maxLines = Math.max(maxLines, totalLines);
    }
  });

  if (containsWrappedText) {
    row.height = maxLines * LINE_HEIGHT_PX;
  }
};

const cellWithTextExists = (worksheet: ExcelJS.Worksheet, searchText: string): boolean => {
  let found = false;
  const lowerSearch = searchText.toLowerCase();

  worksheet.eachRow((row) => {
    row.eachCell((cell) => {
      if (cell.value && cell.value.toString().toLowerCase() === lowerSearch) {
        found = true;
        return;
      }
    });
    if (found) return;
  });

  return found;
};

const appendApartmentTypeGroupHeader = (
  worksheet: ExcelJS.Worksheet,
  livingTypeTitle: string | undefined,
  numberOfColumns: number,
  backgroundColor: string,
  contractLabel?: string,
  contractComparisonLabel?: string
) => {
  const ratesLabel = contractComparisonLabel ? `${contractComparisonLabel} - Rates` : 'Rates';
  const livingTypeRatesTitle = getSectionTitle(livingTypeTitle, ratesLabel, contractLabel);
  if (!livingTypeRatesTitle) {
    return;
  }

  if (cellWithTextExists(worksheet, livingTypeRatesTitle)) {
    return;
  }

  const titleRow = worksheet.addRow([livingTypeRatesTitle]);
  titleRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: backgroundColor },
    };
    cell.font = FONT_STYLES.BOLD;
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'center',
    };
  });

  worksheet.mergeCells(titleRow.number, 1, titleRow.number, numberOfColumns);
  worksheet.getCell(titleRow.number, 1).border = getExceljsStyleObject(BORDERS.HEADER_CELL);
};

const normalizeAreaComparisonData = (
  item: CMADataCellValue,
  index: number,
  filters: CMASummaryFilters,
  unitSystem: UnitSystem
) => {
  const value = getComparisonSeriesValue(item, index);

  if (typeof value === 'number') {
    return normalizeComparisonData(Math.round(convertAreaByUnitSystem(value, unitSystem)), filters);
  }

  if (Array.isArray(value)) {
    return normalizeComparisonData(
      [Math.round(convertAreaByUnitSystem(value[0], unitSystem)), convertAreaByUnitSystem(value[1], unitSystem)],
      filters
    );
  }

  return normalizeComparisonData(value, filters);
};

const appendApartmentTypeGroupDataRows = (
  worksheet: ExcelJS.Worksheet,
  rowMetadata: CMAHeader,
  numberOfColumns: number,
  rowContent: CMADataCellValue[],
  filters: CMASummaryFilters,
  backgroundColor: string,
  unitSystem: UnitSystem,
  areaUnitLabel: string,
  ratePerAreaUnitLabel: string,
  contractLabel?: string,
  contractComparisonLabel?: string
) => {
  const subtitle = Array.isArray(rowMetadata.subtitle) ? rowMetadata.subtitle[0] : rowMetadata.subtitle ?? '';
  appendApartmentTypeGroupHeader(
    worksheet,
    subtitle,
    numberOfColumns,
    backgroundColor,
    contractLabel,
    contractComparisonLabel
  );

  const rateRow = worksheet.addRow([
    '',
    'Rate',
    ...rowContent.map((item) => {
      const rateValue = getComparisonSeriesValue(item, 0);
      return normalizeComparisonData(rateValue, filters, '$');
    }),
  ]);
  rateRow.eachCell((cell, index) => {
    cell.font = FONT_STYLES.DEFAULT;
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'right',
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: hexToARGB(COLORS.APARTMENT_GROUP_DATA_BACKGROUND) },
    };
    cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
    cell.border.top!.style = 'medium';
    if (index === numberOfColumns) {
      cell.border.right!.style = 'medium';
    }
  });
  const sqFtHighlights = rowContent.map((item) => getHighlightFlag(item, 2));
  const sqFtRow = worksheet.addRow([
    '',
    areaUnitLabel,
    ...rowContent.map((item) => {
      return normalizeAreaComparisonData(item, 2, filters, unitSystem);
    }),
  ]);
  sqFtRow.eachCell((cell, index) => {
    const font = { ...FONT_STYLES.DEFAULT };
    if (index >= 3 && sqFtHighlights[index - 3]) {
      font.color = { argb: hexToARGB(HIGHLIGHT_TEXT_COLOR) };
    }
    cell.font = font;
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'right',
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: hexToARGB(COLORS.APARTMENT_GROUP_DATA_BACKGROUND) },
    };
    cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
    if (index === numberOfColumns) {
      cell.border.right!.style = 'medium';
    }
  });
  const ratePerSqFtRow = worksheet.addRow([
    '',
    `Rate ${ratePerAreaUnitLabel}`,
    ...rowContent.map((item) => {
      const rateValue = getNumericSeriesValue(item, 0);
      const sqFtRowValue = getNumericSeriesValue(item, 2);
      if (rateValue === null || sqFtRowValue === null || sqFtRowValue === 0) {
        return '';
      }
      const rawRatePerSqft = rateValue / sqFtRowValue;
      const ratePerSqft = Math.round(convertRatePerAreaByUnitSystem(rawRatePerSqft, unitSystem));
      return isFinite(ratePerSqft) ? `$${ratePerSqft}` : '';
    }),
  ]);
  ratePerSqFtRow.eachCell((cell, index) => {
    cell.font = FONT_STYLES.DEFAULT;
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'right',
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: hexToARGB(COLORS.APARTMENT_GROUP_DATA_BACKGROUND) },
    };
    cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
    cell.border.bottom!.style = 'medium';
    if (index === numberOfColumns) {
      cell.border.right!.style = 'medium';
    }
  });

  worksheet.mergeCells(rateRow.number, 1, ratePerSqFtRow.number, 1);
  const mergedCell = worksheet.getCell(rateRow.number, 1);
  const aggregationType = AGGREGATION_TYPE_LABELS[filters.roomTypesPriceDisplay || AGGREGATION_TYPE.AVG];
  mergedCell.value = `${rowMetadata.title} Rate (${aggregationType})`;
  mergedCell.alignment = { horizontal: 'right', vertical: 'middle', wrapText: true };
  mergedCell.font = FONT_STYLES.DEFAULT;
  mergedCell.fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: hexToARGB(COLORS.APARTMENT_GROUP_DATA_BACKGROUND) },
  };
  mergedCell.border = {
    bottom: { style: 'medium' as ExcelJS.BorderStyle, color: { argb: 'FF000000' } },
    top: { style: 'medium' as ExcelJS.BorderStyle, color: { argb: 'FF000000' } },
  };
};

const appendApartmentTypeDataRows = (
  worksheet: ExcelJS.Worksheet,
  rowMetadata: CMAHeader,
  rowContent: CMADataCellValue[],
  numberOfColumns: number,
  filters: CMASummaryFilters,
  unitSystem: UnitSystem,
  areaUnitLabel: string,
  ratePerAreaUnitLabel: string
) => {
  const rateRow = worksheet.addRow([
    '',
    'Rate',
    ...rowContent.map((item) => {
      const rateValue = getComparisonSeriesValue(item, 0);
      return normalizeComparisonData(rateValue, filters, '$');
    }),
  ]);
  rateRow.eachCell((cell, index) => {
    cell.font = FONT_STYLES.DEFAULT;
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'right',
    };
    cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
    cell.border.top!.style = 'medium';
    if (index === numberOfColumns) {
      cell.border.right!.style = 'medium';
    }
  });
  const sqFtHighlights = rowContent.map((item) => getHighlightFlag(item, 2));
  const sqFtRow = worksheet.addRow([
    '',
    areaUnitLabel,
    ...rowContent.map((item) => {
      return normalizeAreaComparisonData(item, 2, filters, unitSystem);
    }),
  ]);
  sqFtRow.eachCell((cell, index) => {
    const font = { ...FONT_STYLES.DEFAULT };
    if (index >= 3 && sqFtHighlights[index - 3]) {
      font.color = { argb: hexToARGB(HIGHLIGHT_TEXT_COLOR) };
    }
    cell.font = font;
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'right',
    };
    cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
    if (index === numberOfColumns) {
      cell.border.right!.style = 'medium';
    }
  });
  const ratePerSqFtRow = worksheet.addRow([
    '',
    `Rate ${ratePerAreaUnitLabel}`,
    ...rowContent.map((item) => {
      const rateValue = getNumericSeriesValue(item, 0);
      const sqFtRowValue = getNumericSeriesValue(item, 2);
      if (rateValue === null || sqFtRowValue === null || sqFtRowValue === 0) {
        return '';
      }
      const rawRatePerSqft = rateValue / sqFtRowValue;
      const ratePerSqft = Math.round(convertRatePerAreaByUnitSystem(rawRatePerSqft, unitSystem));
      return isFinite(ratePerSqft) ? `$${ratePerSqft}` : '';
    }),
  ]);
  ratePerSqFtRow.eachCell((cell, index) => {
    cell.font = FONT_STYLES.DEFAULT;
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'right',
    };
    cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
    cell.border.bottom!.style = 'medium';
    if (index === numberOfColumns) {
      cell.border.right!.style = 'medium';
    }
  });

  worksheet.mergeCells(rateRow.number, 1, ratePerSqFtRow.number, 1);
  const mergedCell = worksheet.getCell(rateRow.number, 1);
  mergedCell.value = rowMetadata.title;
  mergedCell.alignment = { horizontal: 'right', vertical: 'middle', wrapText: true };
  mergedCell.font = FONT_STYLES.DEFAULT;
  mergedCell.border = { bottom: { style: 'medium' as ExcelJS.BorderStyle, color: { argb: 'FF000000' } } };
};

const getRoomComparisonRowTitle = (rowMetadata: CMAHeader) => {
  const titleParts = Array.isArray(rowMetadata.title) ? rowMetadata.title : [rowMetadata.title].filter(Boolean);
  const subtitleParts = Array.isArray(rowMetadata.subtitle) ? rowMetadata.subtitle : [];

  const comparisonTitle = titleParts
    .map((item, index) => {
      const subtitle =
        item === CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS.CARE ||
        item === CMA_SUMMARY_ROOM_COMPARISON_TYPE_FILTER_ROW_FIELDS.RATE
          ? subtitleParts[index]
          : '';
      return subtitle ? `${item} (${subtitle})` : item;
    })
    .join(' + ');

  const rowTitle =
    rowMetadata.type === CMA_SUMMARY_APARTMENT_TYPE_GROUP_DATA_ROW_ROOM_COMPARISON
      ? (rowMetadata as any).apartmentGroupType
      : (rowMetadata as any).apartmentType;

  return [rowTitle, comparisonTitle].filter(Boolean).join(' ');
};

const appendRoomComparisonRow = (
  worksheet: ExcelJS.Worksheet,
  rowMetadata: CMAHeader,
  rowContent: CMADataCellValue[],
  numberOfColumns: number,
  filters: CMASummaryFilters,
  backgroundColor: string,
  contractLabel?: string,
  contractComparisonLabel?: string
) => {
  appendApartmentTypeGroupHeader(
    worksheet,
    (rowMetadata as any).livingType,
    numberOfColumns,
    backgroundColor,
    contractLabel,
    contractComparisonLabel
  );

  const row = worksheet.addRow([
    getRoomComparisonRowTitle(rowMetadata),
    '',
    ...rowContent.map((item) => {
      const comparisonValue = getComparisonSeriesValue(item, 0);
      return normalizeComparisonData(comparisonValue as any, filters, '$');
    }),
  ]);
  worksheet.mergeCells(row.number, 1, row.number, 2);

  row.eachCell((cell, index) => {
    cell.font = FONT_STYLES.DEFAULT;
    cell.alignment = {
      vertical: 'middle',
      horizontal: index <= 2 ? 'left' : 'right',
      wrapText: index <= 2,
    };
    cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
    if (index === numberOfColumns) {
      cell.border.right!.style = 'medium';
    }
  });
};

const appendContractBenefitsRows = (
  worksheet: ExcelJS.Worksheet,
  rowMetadata: CMAHeader,
  rowContent: CMADataCellValue[]
) => {
  const benefitsByCommunity = rowContent.map((grouped) => {
    if (grouped === CMA_SUMMARY_NOT_APPLICABLE_VALUE) {
      return CMA_SUMMARY_NOT_APPLICABLE_VALUE;
    }

    return isContractBenefitsRecord(grouped) ? grouped : ({} as Record<string, string[]>);
  });

  const orderedCategories: string[] = [];
  const categorySet = new Set<string>();
  benefitsByCommunity.forEach((communityBenefits) => {
    if (communityBenefits === CMA_SUMMARY_NOT_APPLICABLE_VALUE) {
      return;
    }

    Object.keys(communityBenefits).forEach((category) => {
      if (!categorySet.has(category)) {
        categorySet.add(category);
        orderedCategories.push(category);
      }
    });
  });

  const categoryBlocks = orderedCategories
    .map((category) => {
      const rowsCount = benefitsByCommunity.reduce(
        (max, communityBenefits) =>
          communityBenefits === CMA_SUMMARY_NOT_APPLICABLE_VALUE
            ? max
            : Math.max(max, communityBenefits[category]?.length ?? 0),
        0
      );
      return { category, rowsCount };
    })
    .filter((block) => block.rowsCount > 0);

  if (!categoryBlocks.length) {
    return;
  }

  let firstBenefitRowNumber: number | null = null;
  let lastBenefitRowNumber: number | null = null;

  categoryBlocks.forEach(({ category, rowsCount }) => {
    let categoryStartRow: number | null = null;
    let categoryEndRow: number | null = null;

    for (let i = 0; i < rowsCount; i += 1) {
      const row = worksheet.addRow([
        '',
        category,
        ...benefitsByCommunity.map((communityBenefits) =>
          communityBenefits === CMA_SUMMARY_NOT_APPLICABLE_VALUE
            ? CMA_SUMMARY_NOT_APPLICABLE_VALUE
            : communityBenefits[category]?.[i] || ''
        ),
      ]);

      if (firstBenefitRowNumber === null) {
        firstBenefitRowNumber = row.number;
      }
      lastBenefitRowNumber = row.number;

      if (categoryStartRow === null) {
        categoryStartRow = row.number;
      }
      categoryEndRow = row.number;

      row.eachCell((cell, index) => {
        cell.font = FONT_STYLES.DEFAULT;
        cell.alignment = {
          vertical: 'middle',
          horizontal: 'right',
          wrapText: true,
        };
        cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
      });
      adjustRowHeightForWrappedText(row);
    }

    if (categoryStartRow !== null && categoryEndRow !== null && categoryStartRow !== categoryEndRow) {
      worksheet.mergeCells(categoryStartRow, 2, categoryEndRow, 2);
      const categoryCell = worksheet.getCell(categoryStartRow, 2);
      categoryCell.value = category;
      categoryCell.font = FONT_STYLES.DEFAULT;
      categoryCell.alignment = {
        vertical: 'middle',
        horizontal: 'right',
        wrapText: true,
      };
      categoryCell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
    }
  });

  if (firstBenefitRowNumber !== null && lastBenefitRowNumber !== null) {
    worksheet.mergeCells(firstBenefitRowNumber, 1, lastBenefitRowNumber, 1);
    const benefitsCell = worksheet.getCell(firstBenefitRowNumber, 1);
    benefitsCell.value = rowMetadata.title || 'Benefits';
    benefitsCell.font = FONT_STYLES.DEFAULT;
    benefitsCell.alignment = {
      vertical: 'middle',
      horizontal: 'right',
      wrapText: true,
    };
    benefitsCell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
  }
};

const appendContractTypeSummaryRow = (worksheet: ExcelJS.Worksheet, contractTypes: string[]) => {
  if (!contractTypes.length || !contractTypes.some((value) => Boolean(value))) {
    return;
  }

  const row = worksheet.addRow(['Contract Type', '', ...contractTypes]);
  worksheet.mergeCells(row.number, 1, row.number, 2);
  row.eachCell((cell, index) => {
    cell.font = FONT_STYLES.DEFAULT;
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'right',
      wrapText: true,
    };
    cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
  });
};

const appendCommunityFeeRows = (
  worksheet: ExcelJS.Worksheet,
  rowMetadata: CMAHeader,
  numberOfColumns: number,
  rowContent: CMADataCellValue[],
  filters: CMASummaryFilters,
  backgroundColor: string,
  contractLabel?: string
) => {
  const livingTypeRatesTitle = getSectionTitle(
    typeof rowMetadata.subtitle === 'string' ? rowMetadata.subtitle : undefined,
    'Fees',
    contractLabel
  );
  if (!livingTypeRatesTitle) {
    return;
  }
  if (!cellWithTextExists(worksheet, livingTypeRatesTitle)) {
    const titleRow = worksheet.addRow([livingTypeRatesTitle]);
    titleRow.eachCell((cell, index) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: backgroundColor },
      };
      cell.font = FONT_STYLES.BOLD;
      cell.alignment = {
        vertical: 'middle',
        horizontal: 'center',
      };
    });

    worksheet.mergeCells(titleRow.number, 1, titleRow.number, numberOfColumns);
    worksheet.getCell(titleRow.number, 1).border = getExceljsStyleObject(BORDERS.HEADER_CELL);
  }

  const row = worksheet.addRow([
    rowMetadata.title,
    '',
    ...rowContent.map((item) => {
      const feeValue = getComparisonSeriesValue(item, 0);
      return normalizeComparisonData(feeValue as any, filters, '$');
    }),
  ]);
  row.eachCell((cell, index) => {
    cell.font = FONT_STYLES.DEFAULT;
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'right',
    };
    cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
  });
  worksheet.mergeCells(row.number, 1, row.number, 2);
};

const appendFeesGroupRows = (
  worksheet: ExcelJS.Worksheet,
  rowMetadata: CMAHeader,
  numberOfColumns: number,
  rowContent: CMADataCellValue[],
  filters: CMASummaryFilters,
  backgroundColor: string,
  aggregationTypeValue?: number,
  contractLabel?: string
) => {
  const feeTitle = rowMetadata.title || getFeeTitleByRowType(rowMetadata.type);
  const feeLabel = getFeeLabelByRowType(rowMetadata.type);
  const sectionTitle = getSectionTitle(
    typeof rowMetadata.subtitle === 'string' ? rowMetadata.subtitle : undefined,
    feeTitle,
    contractLabel
  );
  if (!sectionTitle) {
    return;
  }
  const titleRow = worksheet.addRow([sectionTitle]);
  titleRow.eachCell((cell, index) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: backgroundColor },
    };
    cell.font = FONT_STYLES.BOLD;
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'center',
    };
  });

  worksheet.mergeCells(titleRow.number, 1, titleRow.number, numberOfColumns);
  worksheet.getCell(titleRow.number, 1).border = getExceljsStyleObject(BORDERS.HEADER_CELL);

  const feeTypeRow = worksheet.addRow([
    `${feeLabel} Type`,
    '',
    ...rowContent.map((item) => {
      if (item === CMA_SUMMARY_NOT_APPLICABLE_VALUE) {
        return CMA_SUMMARY_NOT_APPLICABLE_VALUE;
      }

      if (!Array.isArray(item)) {
        return '';
      }

      const feeValues = item as [number?, string?, string?];
      return feeValues[1];
    }),
  ]);
  worksheet.mergeCells(feeTypeRow.number, 1, feeTypeRow.number, 2);
  feeTypeRow.eachCell((cell, index) => {
    cell.font = FONT_STYLES.DEFAULT;
    cell.alignment = {
      vertical: 'middle',
      horizontal: index >= 3 ? 'left' : 'right',
    };
    cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
  });

  const resolvedAggregationType = aggregationTypeValue ?? filters.careLevelsStatsType ?? AGGREGATION_TYPE.AVG;
  const aggregationType = AGGREGATION_TYPE_LABELS[resolvedAggregationType];
  const feeAggregateRow = worksheet.addRow([
    `${feeLabel} (${aggregationType})`,
    '',
    ...rowContent.map((item) => {
      const numericValue = getNumericSeriesValue(item, 0);
      return typeof numericValue === 'number' ? `$${Math.round(numericValue)}` : '';
    }),
  ]);
  worksheet.mergeCells(feeAggregateRow.number, 1, feeAggregateRow.number, 2);
  feeAggregateRow.eachCell((cell, index) => {
    cell.font = FONT_STYLES.DEFAULT;
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'right',
    };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: hexToARGB(COLORS.APARTMENT_GROUP_DATA_BACKGROUND) },
    };
    cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
  });
  const feeNotes = rowContent.map((item) => {
    const note = (item as [number?, string?, string?, string?])[3];
    return note ? note.trim() : '';
  });

  if (feeNotes.some((n) => n)) {
    const feeNotesRow = worksheet.addRow([`${feeLabel} Notes`, '', ...feeNotes]);

    worksheet.mergeCells(feeNotesRow.number, 1, feeNotesRow.number, 2);
    feeNotesRow.eachCell((cell, index) => {
      cell.font = FONT_STYLES.DEFAULT;
      cell.alignment = {
        vertical: 'middle',
        horizontal: index >= 3 ? 'left' : 'right',
        wrapText: true,
      };
      cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
    });
  }
};

const appendFeeLevelRows = (worksheet: ExcelJS.Worksheet, rowMetadata: CMAHeader, rowContent: CMADataCellValue[]) => {
  const feeLevelRow = worksheet.addRow([
    rowMetadata.title,
    '',
    ...rowContent.map((item) => {
      const numericValue = getNumericSeriesValue(item, 0);
      return typeof numericValue === 'number' ? `$${numericValue}` : '';
    }),
  ]);
  worksheet.mergeCells(feeLevelRow.number, 1, feeLevelRow.number, 2);
  feeLevelRow.eachCell((cell, index) => {
    cell.font = FONT_STYLES.DEFAULT;
    cell.alignment = {
      vertical: 'middle',
      horizontal: 'right',
    };
    cell.border = getExceljsStyleObject(BORDERS.REGULAR_CELL);
  });
};

const normalizeComparisonData = (
  values: number | [number | undefined, number | string | undefined, (string | undefined)?] | undefined,
  filters: CMASummaryFilters,
  prefix = ''
) => {
  let value, comparisonValue;
  if (!Array.isArray(values)) {
    value = values;
  } else if (Array.isArray(values)) {
    value = values[0];
    comparisonValue = values[1];
    if (typeof comparisonValue === 'number' && !Number.isInteger(comparisonValue) && comparisonValue) {
      comparisonValue = parseFloat(comparisonValue.toFixed(1));
    }
  }

  if (typeof value === 'number' && !Number.isInteger(value)) {
    value = parseFloat(value.toFixed(2));
  }

  if (!value) {
    return '';
  }
  let comparisonNumber: number | undefined;
  if (typeof comparisonValue === 'number') {
    comparisonNumber = comparisonValue;
  } else if (typeof comparisonValue === 'string') {
    const trimmedComparison = comparisonValue.trim();
    if (trimmedComparison !== '') {
      comparisonNumber = parseFloat(trimmedComparison);
    }
  }

  const hasComparisonValue =
    comparisonValue !== undefined &&
    comparisonValue !== null &&
    comparisonValue !== '' &&
    !Number.isNaN(comparisonNumber ?? Number.NaN);

  const additionalValuePrefix =
    hasComparisonValue && typeof comparisonNumber === 'number' && comparisonNumber > 0 ? '+' : '';
  const additionalValueSuffix = filters.viewOptions.valueDisplay === VALUE_DIFFERENCE_DISPLAY.PERCENTAGES ? '%' : '';
  const comparisonDisplayValue =
    typeof comparisonValue === 'number' || typeof comparisonValue === 'string' ? comparisonValue : comparisonNumber;
  const additionalValue =
    hasComparisonValue && comparisonDisplayValue !== undefined
      ? ` (${additionalValuePrefix}${comparisonDisplayValue}${additionalValueSuffix})`
      : '';
  return `${prefix}${value}${additionalValue}`;
};
